import React, {useEffect, useRef, useState} from 'react';
import {NavLink} from 'react-router-dom';
import {motion} from 'framer-motion';
import {HeaderLinkItem} from './Header';

interface HeaderNavItemProps {
    item: HeaderLinkItem;
    mainPage?: boolean;
    liStyles?: string;
    burgerMenu?: boolean;
    onClick?: () => void;
    useDarkTheme?: boolean;
}

const linkInactive = 'nav-link p-0 link-secondary-light';
const linkActive = 'nav-link p-0 link-light-light';

const linkActiveBurger = 'nav-link link-light-light bg-dark';
const linkInactiveBurger = 'nav-link link-secondary-light';

export default function HeaderNavItem(props: HeaderNavItemProps) {
    const {item, mainPage, liStyles, burgerMenu, onClick, useDarkTheme} = props;
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLLIElement>(null);

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setDropdownOpen(false);
        }
    };

    useEffect(() => {
        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [dropdownOpen]);

    // If item has subItems, render a dropdown
    if (item.subItems && item.subItems.length > 0) {
        if (burgerMenu) {
            // For mobile menu, show all items in a collapsed format
            return (
                <>
                    <motion.li className={`nav-item ${liStyles ? liStyles : ''}`} whileHover={{scale: 1.1}}>
                        <button
                            className={`${burgerMenu ? 'nav-link link-secondary-light' : 'nav-link p-0 link-secondary-light'} bg-transparent border-0 w-100 text-start d-flex align-items-center justify-content-between`}
                            onClick={toggleDropdown}
                        >
                            {item.title}
                            <motion.span
                                animate={{rotate: dropdownOpen ? 180 : 0}}
                                transition={{duration: 0.2}}
                            >
                                ▼
                            </motion.span>
                        </button>
                    </motion.li>
                    {dropdownOpen && item.subItems.map((subItem) => (
                        <motion.li
                            key={subItem.title}
                            className={`nav-item ${liStyles ? liStyles : ''} ps-3`}
                            whileHover={{scale: 1.05}}
                            initial={{opacity: 0, y: -10}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.2}}
                        >
                            <NavLink
                                className={({isActive}) => (isActive ? linkActiveBurger : linkInactiveBurger)}
                                onClick={onClick}
                                to={subItem.to || '#'}
                                end={mainPage}
                            >
                                • {subItem.title}
                            </NavLink>
                        </motion.li>
                    ))}
                </>
            );
        } else {
            // For desktop menu, new implementation with dropdown menu
            return (
                <motion.li
                    ref={dropdownRef}
                    style={{zIndex: 2000}}
                    className={`nav-item dropdown ${liStyles || ''}`}
                    whileHover={{scale: 1.05}}
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                >
                    <a
                        className="nav-link p-0 link-secondary-light d-flex align-items-center"
                        href="#"
                        role="button"
                        onClick={(e) => {
                            e.preventDefault();
                            toggleDropdown();
                        }}
                    >
                        {item.title}
                        <motion.span
                            className="ms-1"
                            animate={{rotate: dropdownOpen ? 180 : 0}}
                            transition={{duration: 0.2}}
                            style={{fontSize: '0.7em'}}
                        >
                            ▼
                        </motion.span>
                    </a>
                    <motion.ul
                        className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}
                        initial={{opacity: 0, y: -10}}
                        animate={{
                            opacity: dropdownOpen ? 1 : 0,
                            y: dropdownOpen ? 0 : -10,
                            transition: {duration: 0.2}
                        }}
                        style={{
                            display: dropdownOpen ? 'block' : 'none',
                            backgroundColor: useDarkTheme ? 'rgba(26, 26, 46, 0.98)' : '#2a3342',
                            border: useDarkTheme ? '1px solid rgba(42, 209, 103, 0.15)' : '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '8px',
                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
                            padding: '0.5rem 0',
                            minWidth: '220px',
                            zIndex: 2000
                        }}
                    >
                        {item.subItems.map((subItem, index) => (
                            <motion.li
                                key={subItem.title}
                                whileHover={{backgroundColor: 'rgba(42, 209, 103, 0.15)'}}
                                initial={{opacity: 0, x: -20}}
                                animate={{
                                    opacity: 1,
                                    x: 0,
                                    transition: {
                                        delay: index * 0.05,
                                        duration: 0.2
                                    }
                                }}
                            >
                                <NavLink
                                    className={({isActive}) => `dropdown-item d-flex align-items-center ${isActive ? 'bg-success' : ''}`}
                                    to={subItem.to || '#'}
                                    onClick={() => {
                                        setDropdownOpen(false);
                                        if (onClick) onClick();
                                    }}
                                    style={{
                                        color: '#f8f9fa',
                                        padding: '0.75rem 1.25rem',
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        borderLeft: '3px solid transparent',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderLeft = '3px solid #2ad167';
                                        e.currentTarget.style.backgroundColor = 'rgba(42, 209, 103, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderLeft = '3px solid transparent';
                                        e.currentTarget.style.backgroundColor = '';
                                    }}
                                >
                                    {subItem.title}
                                </NavLink>
                            </motion.li>
                        ))}
                    </motion.ul>
                </motion.li>
            );
        }
    }

    // If no subItems, render a regular navigation item
    return (
        <motion.li className={`nav-item ${liStyles ? liStyles : ''}`} whileHover={{scale: 1.1}}>
            <NavLink
                className={({isActive}) => (isActive ? (burgerMenu ? linkActiveBurger : linkActive) : (burgerMenu ? linkInactiveBurger : linkInactive))}
                onClick={onClick}
                to={item.to || '#'}
                end={mainPage}
            >
                {item.title}
            </NavLink>
        </motion.li>
    );
}
